import { useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
    Box,
    Typography,
    Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableViewIcon from '@mui/icons-material/TableView';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

function formatValue(value, column) {
    if (!value) return '';
    
    // Special handling for MonitoringCCTV data
    if (column.parent === 'MonitoringCCTV') {
        if (column.id === 'status') {
            return value.status || '';
        } else if (column.id === 'ip_address') {
            return value.ip_address || '';
        }
        return '';
    }
    
    // Handle location data
    if (column.id === 'lokasi') {
        try {
            const coords = JSON.parse(value);
            return `lat: ${coords[0]}, long: ${coords[1]}`;
        } catch (error) {
            return '';
        }
    }
    
    return value;
}

function downloadCSV(columns, rows, filename) {
    // Create header row
    const headers = columns.map(col => col.label).join(',');
    
    // Create data rows
    const csvRows = rows.map(row => {
        return columns.map(col => {
            const value = formatValue(row[col.id], col);
            // Escape commas and quotes in values
            const escaped = String(value).replace(/"/g, '""');
            return `"${escaped}"`;
        }).join(',');
    });
    
    // Combine headers and data
    const csvContent = [headers, ...csvRows].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

function downloadJSON(columns, rows, filename) {
    const jsonData = rows.map(row => {
        const obj = {};
        columns.forEach(col => {
            obj[col.label] = formatValue(row[col.id], col);
        });
        return obj;
    });
    
    const jsonContent = JSON.stringify(jsonData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename.replace('.csv', '.json'));
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

export default function DownloadTable({ columns, rows, filename }) {
    const [open, setOpen] = useState(false);
    const [format, setFormat] = useState('csv');
    const [downloading, setDownloading] = useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setFormat('csv');
    };

    const handleDownload = async () => {
        setDownloading(true);
        
        try {
            await new Promise(resolve => setTimeout(resolve, 500)); // Small delay for UX
            
            if (format === 'csv') {
                downloadCSV(columns, rows, filename);
            } else if (format === 'json') {
                downloadJSON(columns, rows, filename);
            }
        } catch (error) {
            console.error('Error downloading file:', error);
        } finally {
            setDownloading(false);
            handleClose();
        }
    };

    const getFormatIcon = (formatType) => {
        switch (formatType) {
            case 'csv':
                return <TableViewIcon sx={{ fontSize: 20, mr: 1 }} />;
            case 'json':
                return <FileDownloadIcon sx={{ fontSize: 20, mr: 1 }} />;
            case 'pdf':
                return <PictureAsPdfIcon sx={{ fontSize: 20, mr: 1 }} />;
            default:
                return <FileDownloadIcon sx={{ fontSize: 20, mr: 1 }} />;
        }
    };

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={handleClickOpen}
                size="small"
                sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2
                }}
            >
                Export
            </Button>

            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DownloadIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6" fontWeight={600}>
                            Export Data Monitoring CCTV
                        </Typography>
                    </Box>
                </DialogTitle>
                
                <Divider />
                
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Data yang akan diexport:
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {rows.length} baris data monitoring CCTV
                        </Typography>
                    </Box>

                    <FormControl component="fieldset" fullWidth>
                        <FormLabel 
                            component="legend" 
                            sx={{ 
                                fontWeight: 600, 
                                color: 'text.primary',
                                mb: 2
                            }}
                        >
                            Pilih Format Export:
                        </FormLabel>
                        <RadioGroup
                            value={format}
                            onChange={(e) => setFormat(e.target.value)}
                        >
                            <FormControlLabel
                                value="csv"
                                control={<Radio color="primary" />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {getFormatIcon('csv')}
                                        <Box>
                                            <Typography variant="body1" fontWeight={500}>
                                                CSV (Excel Compatible)
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Format tabel yang bisa dibuka di Excel
                                            </Typography>
                                        </Box>
                                    </Box>
                                }
                                sx={{ 
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: format === 'csv' ? 'primary.main' : 'grey.300',
                                    borderRadius: 2,
                                    p: 2,
                                    m: 0,
                                    backgroundColor: format === 'csv' ? 'primary.light' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: format === 'csv' ? 'primary.light' : 'grey.50'
                                    }
                                }}
                            />
                            <FormControlLabel
                                value="json"
                                control={<Radio color="primary" />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {getFormatIcon('json')}
                                        <Box>
                                            <Typography variant="body1" fontWeight={500}>
                                                JSON (JavaScript Object)
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Format data untuk developer/programmer
                                            </Typography>
                                        </Box>
                                    </Box>
                                }
                                sx={{ 
                                    border: '1px solid',
                                    borderColor: format === 'json' ? 'primary.main' : 'grey.300',
                                    borderRadius: 2,
                                    p: 2,
                                    m: 0,
                                    backgroundColor: format === 'json' ? 'primary.light' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: format === 'json' ? 'primary.light' : 'grey.50'
                                    }
                                }}
                            />
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                
                <Divider />
                
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={handleClose}
                        variant="outlined"
                        sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 3
                        }}
                    >
                        Batal
                    </Button>
                    <Button
                        onClick={handleDownload}
                        variant="contained"
                        disabled={downloading}
                        startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                        sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 3,
                            minWidth: 120
                        }}
                    >
                        {downloading ? 'Downloading...' : 'Download'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
